import {
  addDays, addWeeks, differenceInCalendarWeeks, getISOWeek, isWeekend, parseISO, startOfWeek,
} from 'date-fns';
import { keyBy } from 'lodash';
import { formatDate, formatDateToISOString } from './dates';
import type {
  Desk, DeskBlock, DeskBooking, DeskBookingRange,
} from '../types';

export type DeskStatus = 'free' | 'mine' | 'taken' | 'blocked' | 'inactive';

export type DeskDayState = 'free' | 'mine' | 'taken' | 'blocked' | 'holiday' | 'disabled';

export type BookingIndex = {
  desksById: Record<number, Desk>,
  byDeskDate: Record<string, DeskBooking>,
  byUserDate: Record<string, DeskBooking>,
  blocksByDeskDate: Record<string, DeskBlock>,
};

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

// Key into the per-day lookups of BookingIndex; the id is a desk id or a user id.
export const dayKey = (id: number, date: string): string => `${id}|${date}`;

export const indexBookings = (desks: Array<Desk>, bookings: Array<DeskBooking>, blocks: Array<DeskBlock>): BookingIndex => ({
  desksById: keyBy(desks, 'id'),
  byDeskDate: keyBy(bookings, (booking) => dayKey(booking.deskId, booking.date)),
  byUserDate: keyBy(bookings, (booking) => dayKey(booking.userId, booking.date)),
  blocksByDeskDate: keyBy(blocks, (block) => dayKey(block.deskId, block.date)),
});

// Mon–Fri weeks covering the window; on a weekend the window starts with next week.
export const buildWeeks = (range: DeskBookingRange): Array<Array<string>> => {
  const from = parseISO(range.from);
  const firstMonday = addWeeks(startOfWeek(from, WEEK_OPTIONS), isWeekend(from) ? 1 : 0);
  const weekCount = differenceInCalendarWeeks(parseISO(range.to), firstMonday, WEEK_OPTIONS) + 1;

  return Array.from({ length: weekCount }, (_w, week) => (
    Array.from({ length: 5 }, (_d, day) => formatDateToISOString(addDays(addWeeks(firstMonday, week), day)))
  ));
};

const isWithinRange = (date: string, range: DeskBookingRange): boolean => date >= range.from && date <= range.to;

export const isBookableDate = (date: string, range: DeskBookingRange, holidays: Array<string>): boolean => (
  isWithinRange(date, range) && !isWeekend(parseISO(date)) && !holidays.includes(date)
);

export const getFirstBookableDate = (range: DeskBookingRange, holidays: Array<string>): string => {
  let date = range.from;

  while (date <= range.to) {
    if (isBookableDate(date, range, holidays)) return date;
    date = formatDateToISOString(addDays(parseISO(date), 1));
  }

  return range.from;
};

// Whether the desk is blocked or booked that day, from the target user's point of view.
const getBookingState = (deskId: number, date: string, index: BookingIndex, targetUserId: number): 'blocked' | 'mine' | 'taken' | null => {
  if (index.blocksByDeskDate[dayKey(deskId, date)]) return 'blocked';

  const booking = index.byDeskDate[dayKey(deskId, date)];
  if (!booking) return null;

  return booking.userId === targetUserId ? 'mine' : 'taken';
};

export const getDeskStatus = (desk: Desk, date: string, index: BookingIndex, targetUserId: number): DeskStatus => {
  if (!desk.active) return 'inactive';

  return getBookingState(desk.id, date, index, targetUserId) || 'free';
};

export const getDayState = (
  deskId: number,
  date: string,
  index: BookingIndex,
  targetUserId: number,
  range: DeskBookingRange,
  holidays: Array<string>,
): DeskDayState => {
  const bookingState = getBookingState(deskId, date, index, targetUserId);
  if (bookingState) return bookingState;
  if (holidays.includes(date)) return 'holiday';

  return isBookableDate(date, range, holidays) ? 'free' : 'disabled';
};

export type WeekMeta = {
  title: string,
  dates: string,
  relative: string,
  isCurrent: boolean,
};

// Labels for the week column, e.g. "Week 43", "19–23 Oct", "This week".
export const getWeekMeta = (week: Array<string>, today: string): WeekMeta => {
  const monday = parseISO(week[0]);
  const friday = parseISO(week[week.length - 1]);
  const offset = differenceInCalendarWeeks(monday, parseISO(today), WEEK_OPTIONS);
  const sameMonth = monday.getMonth() === friday.getMonth();
  let relative = `In ${offset} weeks`;
  if (offset === 0) relative = 'This week';
  else if (offset === 1) relative = 'Next week';

  return {
    title: `Week ${getISOWeek(monday)}`,
    dates: sameMonth
      ? `${formatDate(monday, 'd')}–${formatDate(friday, 'd MMM')}`
      : `${formatDate(monday, 'd MMM')} – ${formatDate(friday, 'd MMM')}`,
    relative,
    isCurrent: offset === 0,
  };
};

// Moves to the previous/next weekday; stays put if that would leave the window.
export const stepWorkday = (date: string, direction: 1 | -1, range: DeskBookingRange): string => {
  let next = addDays(parseISO(date), direction);
  while (isWeekend(next)) next = addDays(next, direction);

  const iso = formatDateToISOString(next);

  return isWithinRange(iso, range) ? iso : date;
};
